<template>
  <gas-page-shell :breadcrumb="['报表管理', '日报表']">
    <div class="stat-row">
      <div v-for="item in statCards" :key="item.label" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value">{{ item.value }}<span v-if="item.unit" class="unit">{{ item.unit }}</span></div>
        </div>
      </div>
    </div>

    <div class="search-panel">
      <el-form :inline="true" :model="query" size="small" label-width="72px" class="search-form">
        <el-form-item label="报表日期">
          <el-date-picker v-model="query.reportDate" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" style="width:160px" />
          <el-button class="date-shortcut" size="mini" @click="shiftReportDate(-1)">前一天</el-button>
          <el-button class="date-shortcut" size="mini" @click="shiftReportDate(1)">后一天</el-button>
        </el-form-item>
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:200px">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="primary" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <div class="table-title-group">
          <span class="table-title">流量计数据报表 <em>共 {{ total }} 条记录</em></span>
          <gas-table-toolbar
            v-if="!useTypedMetricColumns"
            :columns="tableColumnDefs"
            :visible-map.sync="tableColumnVisible"
            :selected-count="0"
            :show-batch-delete="false"
          />
        </div>
        <el-button type="success" size="small" icon="el-icon-download" @click="exportExcel">导出Excel</el-button>
      </div>
      <el-table
        :data="reportListRows"
        :key="useTypedMetricColumns ? ('typed-' + (query.deviceType || '') + '-' + (query.protocolId || '')) : 'flow'"
        v-loading="loading"
        border
        size="small"
        class="report-table"
        :row-key="reportListRowKey"
        :row-class-name="reportRowClassName"
        :span-method="reportSpanMethod"
      >
        <el-table-column v-if="colVisible('rowNo')" label="序号" width="60" align="center">
          <template slot-scope="{ row, $index }">{{ isReportSummaryRow(row) ? '' : rowIndex($index, row) }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('reportDate')" column-key="reportDate" prop="reportDate" label="时间" width="110" />
        <el-table-column v-if="colVisible('stationName')" column-key="siteName" label="站点名称" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ reportSiteName(row) || '—' }}</template>
        </el-table-column>
        <el-table-column v-if="colVisible('siteName')" column-key="deviceName" label="设备名称" min-width="140">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">{{ reportDeviceName(row) }}</span>
            <a v-else class="site-link" href="javascript:;" @click.prevent="openDetail(row)">{{ reportDeviceName(row) }}</a>
          </template>
        </el-table-column>
        <el-table-column v-if="showReportSubDevice && colVisible('subDevice')" column-key="subDevice" label="子设备" min-width="100" align="center">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">{{ row._subLabel || '—' }}</span>
            <a v-else class="site-link" href="javascript:;" @click.prevent="openDetail(row)">{{ row._subLabel || '—' }}</a>
          </template>
        </el-table-column>
        <template v-if="useTypedMetricColumns">
          <el-table-column
            v-for="m in reportMetricColumns"
            :key="m.key"
            :label="formatMetricColumnLabel(m)"
            :min-width="m.width || 120"
            align="right"
          >
            <template slot-scope="{ row }">{{ formatNum(reportMetricValue(row, m)) }}</template>
          </el-table-column>
        </template>
        <template v-else>
          <el-table-column v-if="colVisible('stdTotal')" prop="stdTotal" label="标况累积(m³)" min-width="120" align="right" />
          <el-table-column v-if="colVisible('stdInstant')" prop="stdInstant" label="标况瞬时(m³/h)" min-width="130" align="right" />
          <el-table-column v-if="colVisible('workTotal')" prop="workTotal" label="工况累积(m³)" min-width="120" align="right" />
          <el-table-column v-if="colVisible('workInstant')" prop="workInstant" label="工况瞬时(m³/h)" min-width="130" align="right" />
          <el-table-column v-if="colVisible('pressure')" prop="pressure" label="压力(Kpa)" width="100" align="right" />
          <el-table-column v-if="colVisible('temperature')" prop="temperature" label="温度(℃)" width="90" align="right" />
        </template>
        <el-table-column v-if="colVisible('recordStatus')" label="状态" width="96" align="center">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">—</span>
            <gas-status-tag v-else kind="reportRecord" :value="row.recordStatus" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" fixed="right" align="center">
          <template slot-scope="{ row }">
            <template v-if="!isReportSummaryRow(row)">
              <el-button type="text" size="small" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
              <el-button type="text" size="small" icon="el-icon-download" @click="exportRow(row)">导出</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="canLoadCustomReport"
        class="pager" background layout="total, sizes, prev, pager, next, jumper"
        :total="total" :current-page="query.pageNum" :page-size="query.pageSize"
        :page-sizes="[10, 20, 50, 100]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <el-drawer
      :title="detailTitle"
      :visible.sync="detailVisible"
      direction="rtl"
      size="88%"
      append-to-body
      :wrapper-closable="true"
      custom-class="daily-detail-drawer"
      @closed="onDetailClosed"
    >
      <div class="detail-drawer-body" v-loading="detailLoading">
        <div class="detail-scroll">
          <div v-if="detail" class="detail-info">
            <el-descriptions :column="3" border size="small">
              <el-descriptions-item label="报表日期">{{ dateText(detail.reportDate) }}</el-descriptions-item>
              <el-descriptions-item label="站点名称">{{ reportSiteName(detail) || '—' }}</el-descriptions-item>
              <el-descriptions-item label="所属区域">{{ detail.regionName || '—' }}</el-descriptions-item>
              <el-descriptions-item label="站点类型">{{ detail.siteType || '—' }}</el-descriptions-item>
              <el-descriptions-item label="设备名称">{{ reportDeviceName(detail) || '—' }}</el-descriptions-item>
              <el-descriptions-item v-if="showReportSubDevice" label="子设备">{{ detail.subDeviceLabel || '—' }}</el-descriptions-item>
              <el-descriptions-item label="报表类型">{{ reportTypeLabel(detail.reportType) }}</el-descriptions-item>
              <el-descriptions-item label="状态"><gas-status-tag kind="reportRecord" :value="detail.recordStatus" /></el-descriptions-item>
              <template v-if="useTypedMetricColumns">
                <el-descriptions-item v-for="m in reportMetricColumns" :key="'d-' + m.key" :label="m.label">
                  {{ formatNum(reportMetricValue(detail, m)) }}<template v-if="m.unit"> {{ m.unit }}</template>
                </el-descriptions-item>
              </template>
              <template v-else>
                <el-descriptions-item label="标况累积">{{ formatNum(detail.stdTotal) }} m³</el-descriptions-item>
                <el-descriptions-item label="标况瞬时">{{ formatNum(detail.stdInstant) }} m³/h</el-descriptions-item>
                <el-descriptions-item label="工况累积">{{ formatNum(detail.workTotal) }} m³</el-descriptions-item>
                <el-descriptions-item label="工况瞬时">{{ formatNum(detail.workInstant) }} m³/h</el-descriptions-item>
                <el-descriptions-item label="压力">{{ formatNum(detail.pressure) }} Kpa</el-descriptions-item>
                <el-descriptions-item label="温度">{{ formatNum(detail.temperature) }} ℃</el-descriptions-item>
              </template>
            </el-descriptions>
          </div>

          <div class="hourly-title-row">
            <span class="table-title">24小时统计数据 <em>共 {{ hourlyTotal }} 条记录</em></span>
            <el-button type="success" size="small" icon="el-icon-download" :disabled="!detail" @click="exportHourly">导出24小时数据</el-button>
          </div>

          <el-table
            :data="hourlyList"
            v-loading="hourlyLoading"
            border
            size="small"
            :height="hourlyTableHeight"
            class="hourly-table"
            header-cell-class-name="hourly-table-header"
          >
            <el-table-column label="序号" width="60" align="center">
              <template slot-scope="{ $index }">{{ $index + 1 }}</template>
            </el-table-column>
            <el-table-column label="小时区间" width="90" align="center">
              <template slot-scope="{ row }">{{ formatHourRange(row) }}</template>
            </el-table-column>
            <template v-if="useTypedMetricColumns">
              <el-table-column
                v-for="m in reportMetricColumns"
                :key="'h-' + m.key"
                :label="formatMetricColumnLabel(m)"
                :min-width="m.width || 120"
                align="right"
              >
                <template slot-scope="{ row }">{{ formatNum(reportMetricValue(row, m)) }}</template>
              </el-table-column>
            </template>
            <template v-else>
              <el-table-column prop="stdTotal" label="标况累积(m³)" min-width="120" align="right" />
              <el-table-column prop="stdInstant" label="标况瞬时(m³/h)" min-width="130" align="right" />
              <el-table-column prop="workTotal" label="工况累积(m³)" min-width="120" align="right" />
              <el-table-column prop="workInstant" label="工况瞬时(m³/h)" min-width="130" align="right" />
              <el-table-column prop="pressure" label="压力(Kpa)" width="100" align="right" />
              <el-table-column prop="temperature" label="温度(℃)" width="90" align="right" />
            </template>
            <el-table-column label="状态" width="96" align="center">
              <template slot-scope="{ row }"><gas-status-tag kind="reportRecord" :value="row.recordStatus" /></template>
            </el-table-column>
          </el-table>
        </div>

        <div class="detail-footer">
          <el-button @click="detailVisible = false">关闭</el-button>
        </div>
      </div>
    </el-drawer>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasTableEnhance from '@/mixins/gasTableEnhance'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import { REPORT_DAILY_COLUMNS } from '@/config/tableColumnPresets'
import { gasApi } from '@/api/gas'
import { resolveStatus } from '@/utils/statusTag'
import { formatDateText } from '@/mixins/reportFlowCommon'
import reportTableMerge from '@/mixins/reportTableMerge'
import reportProtocolFilter from '@/mixins/reportProtocolFilter'

export default {
  components: { GasPageShell },
  mixins: [gasTableEnhance, deviceTypeOptionsMixin, siteOptionsMixin, reportTableMerge, reportProtocolFilter],
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      detailVisible: false,
      detailLoading: false,
      hourlyLoading: false,
      detail: null,
      hourlyList: [],
      hourlyTotal: 0,
      hourlyQuery: {
        pageNum: 1,
        pageSize: 24
      },
      projectCategoryOptions: [],
      statCards: [],
      query: {
        pageNum: 1,
        pageSize: 10,
        reportDate: formatDateText(),
        projectCategoryName: '',
        deviceType: '',
        protocolId: null,
        protocolName: '',
        siteName: '',
        deviceName: ''
      }
    }
  },
  created() {
    this.initTableColumns(REPORT_DAILY_COLUMNS, 'report-daily-v2')
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadProjectCategoryOptions()
    this.loadStats()
    this.loadData()
  },
  computed: {
    canLoadCustomReport() {
      return true
    },
    useTypedMetricColumns() {
      return false
    },
    detailTitle() {
      if (!this.detail) return '日报详情'
      const name = this.reportDeviceName(this.detail) || '未命名设备'
      const sub = this.showReportSubDevice ? (this.detail.subDeviceLabel || this.detail._subLabel) : ''
      return sub ? `日报详情 — ${name} / ${sub}` : `日报详情 — ${name}`
    },
    hourlyTableHeight() {
      const h = typeof window !== 'undefined' ? window.innerHeight : 900
      return Math.max(280, h - 360)
    }
  },
  methods: {
    rowIndex(index) {
      return (this.query.pageNum - 1) * this.query.pageSize + index + 1
    },
    formatHourRange(row) {
      if (row && row.hour !== null && row.hour !== undefined && row.hour !== '') {
        return `${Number(row.hour)}点`
      }
      return (row && row.hourRange) || '—'
    },
    reportTypeLabel(v) {
      if (v === 'regulator') return '调压设备报表'
      return '流量计报表'
    },
    formatNum(v) {
      if (v === null || v === undefined || v === '') return '—'
      return v
    },
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => { if (p[k] === '' || p[k] === null) p[k] = undefined })
      return p
    },
    projectCategoryLabel(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    projectCategoryValue(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    async loadProjectCategoryOptions() {
      try {
        const res = await gasApi.projectCategory.options()
        const rows = res.data || []
        this.projectCategoryOptions = rows.map(this.projectCategoryValue).filter(Boolean)
      } catch (e) {
        this.projectCategoryOptions = []
      }
    },
    async loadStats() {
      const res = await gasApi.reportFlowDaily.stats(this.buildQuery())
      const d = res.data || {}
      this.statCards = [
        { label: '设备总数', value: d.totalSites || 0, icon: 'el-icon-s-home', color: '#1677ff', bg: '#e8f3ff' },
        { label: '调压设备', value: d.regulatorCount || 0, icon: 'el-icon-s-grid', color: '#00b42a', bg: '#e8ffea' },
        { label: '流量计', value: d.flowMeterCount || 0, icon: 'el-icon-odometer', color: '#722ed1', bg: '#f9f0ff' },
        { label: '今日用气量', value: (d.todayGasUsage || 0).toLocaleString(), unit: 'm³', icon: 'el-icon-s-opportunity', color: '#ff7d00', bg: '#fff7e8' },
        { label: '异常设备', value: d.abnormalSites || 0, icon: 'el-icon-warning-outline', color: '#f53f3f', bg: '#ffece8' }
      ]
    },
    async loadData() {
      if (!this.canLoadCustomReport) {
        this.list = []
        this.total = 0
        return
      }
      this.loading = true
      try {
        const res = await gasApi.reportFlowDaily.list(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadStats()
      this.loadData()
    },
    resetSearch() {
      this.query = {
        pageNum: 1,
        pageSize: 10,
        reportDate: formatDateText(),
        projectCategoryName: '',
        deviceType: '',
        protocolId: null,
        protocolName: '',
        siteName: '',
        deviceName: ''
      }
      this.customProtocolMetrics = []
      this.loadStats()
      this.loadData()
    },
    shiftReportDate(days) {
      const base = this.query.reportDate ? new Date(`${this.query.reportDate}T00:00:00`) : new Date()
      base.setDate(base.getDate() + days)
      const y = base.getFullYear()
      const m = String(base.getMonth() + 1).padStart(2, '0')
      const d = String(base.getDate()).padStart(2, '0')
      this.query.reportDate = `${y}-${m}-${d}`
      this.search()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    async openDetail(row) {
      if (this.isReportSummaryRow(row)) return
      this.detail = { ...row }
      this.detailVisible = true
      this.hourlyQuery.pageNum = 1
      this.hourlyQuery.pageSize = 24
      await this.loadHourlyData(true)
    },
    async loadHourlyData(loadDetail) {
      if (!this.detail || !this.detail.recordId) return
      const clicked = {
        subDeviceCode: this.detail.subDeviceCode || this.detail._subCode || '',
        subDeviceLabel: this.detail.subDeviceLabel || this.detail._subLabel || ''
      }
      if (loadDetail) this.detailLoading = true
      this.hourlyLoading = true
      try {
        const res = await gasApi.reportFlowDaily.hourly(this.detail.recordId, {
          ...this.hourlyQuery,
          subDeviceCode: clicked.subDeviceCode || undefined
        })
        const data = res.data || {}
        this.detail = Object.assign({}, this.detail, data.detail || {}, {
          subDeviceCode: (data.detail && data.detail.subDeviceCode) || clicked.subDeviceCode,
          subDeviceLabel: (data.detail && data.detail.subDeviceLabel) || clicked.subDeviceLabel
        })
        this.hourlyList = data.records || []
        this.hourlyTotal = Number(data.total || 0)
      } finally {
        this.hourlyLoading = false
        this.detailLoading = false
      }
    },
    onDetailClosed() {
      this.detail = null
      this.hourlyList = []
      this.hourlyTotal = 0
      this.hourlyQuery.pageNum = 1
      this.hourlyQuery.pageSize = 24
    },
    async exportExcel() {
      if (!this.canLoadCustomReport) return this.$message.warning('请先选择协议')
      const res = await gasApi.reportFlowDaily.exportHourly(this.buildQuery())
      this.downloadHourlyCsv(res.data || [], '日报小时数据_流量计数据报表')
    },
    async exportRow(row) {
      if (!row.recordId) return this.downloadCsv([row], `报表_${this.reportDeviceName(row)}`)
      const res = await gasApi.reportFlowDaily.hourly(row.recordId, {
        pageNum: 1,
        pageSize: 24,
        subDeviceCode: row.subDeviceCode || row._subCode || undefined
      })
      const data = res.data || {}
      this.downloadHourlyCsv(data.records || [], `日报小时数据_${this.reportDeviceName(row)}_${this.dateText(row.reportDate)}`)
    },
    async exportHourly() {
      if (!this.detail || !this.detail.recordId) return this.$message.warning('暂无可导出的详情数据')
      const res = await gasApi.reportFlowDaily.hourly(this.detail.recordId, {
        pageNum: 1,
        pageSize: 24,
        subDeviceCode: this.detail.subDeviceCode || this.detail._subCode || undefined
      })
      const data = res.data || {}
      this.downloadHourlyCsv(data.records || [], `日报小时数据_${this.reportDeviceName(this.detail) || ''}_${this.detail.subDeviceLabel || ''}_${this.dateText(this.detail.reportDate)}`)
    },
    downloadHourlyCsv(rows, filename) {
      if (!rows.length) return this.$message.warning('暂无数据可导出')
      const headers = ['报表日期', '小时区间', '设备名称', '子设备', '标况累积(m³)', '标况瞬时(m³/h)', '工况累积(m³)', '工况瞬时(m³/h)', '压力(Kpa)', '温度(℃)', '状态']
      const lines = [headers.join(',')]
      rows.forEach(r => {
        lines.push([
          this.dateText(r.reportDate), this.formatHourRange(r), this.reportDeviceName(r), r.subDeviceLabel || '', r.stdTotal, r.stdInstant, r.workTotal, r.workInstant,
          r.pressure, r.temperature, this.statusLabel(r.recordStatus)
        ].map(this.csvCell).join(','))
      })
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.safeFileName(filename)}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
      this.$message.success('导出成功')
    },
    downloadCsv(rows, filename) {
      if (!rows.length) return this.$message.warning('暂无数据可导出')
      const metrics = this.useTypedMetricColumns ? this.reportMetricColumns : []
      const showSub = this.showReportSubDevice
      const headers = this.useTypedMetricColumns
        ? ['时间', '站点名称', '设备名称', ...(showSub ? ['子设备'] : []), ...metrics.map(m => this.formatMetricColumnLabel(m)), '状态']
        : ['时间', '站点名称', '设备名称', '子设备', '标况累积(m³)', '标况瞬时(m³/h)', '工况累积(m³)', '工况瞬时(m³/h)', '压力(Kpa)', '温度(℃)', '状态']
      const lines = [headers.join(',')]
      rows.forEach(r => {
        const cells = this.useTypedMetricColumns
          ? [this.dateText(r.reportDate), this.reportSiteName(r), this.reportDeviceName(r),
            ...(showSub ? [r.subDeviceLabel || r._subLabel || ''] : []),
            ...metrics.map(m => this.reportMetricValue(r, m)), this.statusLabel(r.recordStatus)]
          : [
            this.dateText(r.reportDate), this.reportSiteName(r), this.reportDeviceName(r), r.subDeviceLabel || '', r.stdTotal, r.stdInstant, r.workTotal, r.workInstant,
            r.pressure, r.temperature, this.statusLabel(r.recordStatus)
          ]
        lines.push(cells.map(this.csvCell).join(','))
      })
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.safeFileName(filename)}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
      this.$message.success('导出成功')
    },
    csvCell(v) {
      const text = v === null || v === undefined ? '' : String(v)
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    },
    statusLabel(value) {
      return resolveStatus('reportRecord', value).label
    },
    dateText(v) {
      if (!v) return ''
      return String(v).slice(0, 10)
    },
    safeFileName(name) {
      return String(name || '导出数据').replace(/[\\/:*?"<>|]/g, '_')
    },
    reportDeviceName(row) {
      if (!row) return ''
      if (row._summaryKind === 'site') return '合计'
      return row.deviceName || row.siteName || row.deviceCode || row.terminalCode || '-'
    }
  }
}
</script>

<style scoped>
.search-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
}
.search-form >>> .el-form-item {
  margin-bottom: 12px;
}
.date-shortcut {
  margin-left: 8px;
}
.stat-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}
.stat-label {
  color: #86909c;
  font-size: 13px;
}
.stat-value {
  font-size: 22px;
  font-weight: 600;
  color: #1d2129;
  margin-top: 4px;
}
.unit {
  font-size: 12px;
  font-weight: 400;
  margin-left: 4px;
  color: #86909c;
}
.table-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}
.table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.table-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.table-title em {
  font-style: normal;
  font-weight: 400;
  color: #86909c;
  font-size: 13px;
  margin-left: 8px;
}
.site-link {
  color: #722ed1;
  text-decoration: none;
}
.site-link:hover {
  text-decoration: underline;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.detail-drawer-body {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}
.detail-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 0 20px 12px;
  box-sizing: border-box;
}
.detail-info {
  margin-bottom: 16px;
}
.hourly-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.hourly-table >>> .hourly-table-header {
  background: #4e5969 !important;
  color: #fff;
  font-weight: 500;
}
.hourly-table >>> th.hourly-table-header .cell {
  color: #fff;
}
.detail-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid #e5e6eb;
  background: #fff;
}
@media (max-width: 1200px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>

<style>
.report-table tr.report-summary-row > td {
  background: #f7f8fa !important;
  font-weight: 600;
  color: #1d2129;
}
.report-table tr.report-summary-row.is-site > td {
  background: #eef4ff !important;
}
.daily-detail-drawer {
  height: 100% !important;
}
.daily-detail-drawer .el-drawer__header {
  margin-bottom: 12px;
  padding: 16px 20px 0;
}
.daily-detail-drawer .el-drawer__body {
  height: calc(100% - 55px);
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}
</style>
